import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, IndianRupee, FileText, AlertCircle } from 'lucide-react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { MessageModal } from '../../components/modals/MessageModal';
import { api } from '../../services/api';
export function MakeOfferPage() {
  const navigate = useNavigate();
  const {
    listingId
  } = useParams();
  const [offerAmount, setOfferAmount] = useState('');
  const [message, setMessage] = useState('');
  const [earnestMoney, setEarnestMoney] = useState('10');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const listingPrice = 4500000; // ₹45 Lakhs
  const offerValue = parseFloat(offerAmount) * 100000 || 0;
  const earnestMoneyAmount = offerValue * parseFloat(earnestMoney) / 100;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (offerValue < listingPrice * 0.8) {
      alert('Offer must be at least 80% of listing price');
      return;
    }
    setIsSubmitting(true);
    try {
      await api.transactions.createOffer({
        listingId,
        amount: offerValue,
        earnestMoney: earnestMoneyAmount,
        message
      });
      setIsMessageModalOpen(true);
      setTimeout(() => {
        navigate('/buyer/inquiries');
      }, 2000);
    } catch (error) {
      console.error('Error creating offer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <DashboardLayout title="Make Offer" navItems={[]} data-id="element-5765">
      <div className="max-w-3xl mx-auto" data-id="element-5766">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6" data-id="element-5767">
          <ArrowLeft className="w-4 h-4" data-id="element-5768" />
          Back to Listing
        </button>

        <div className="bg-white border border-gray-300 p-8" data-id="element-5769">
          <h1 className="text-2xl font-bold mb-6" data-id="element-5770">Make an Offer</h1>

          {/* Listing Info */}
          <div className="p-4 bg-gray-50 border border-gray-200 mb-6" data-id="element-5771">
            <p className="text-sm text-gray-600 mb-1" data-id="element-5772">Property:</p>
            <p className="font-bold" data-id="element-5773">
              Premium Corner Plot in Hinjewadi Phase 1
            </p>
            <p className="text-sm text-gray-600 mt-2" data-id="element-5774">Listing Price:</p>
            <p className="font-bold text-lg" data-id="element-5775">₹45,00,000</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-5776">
            {/* Offer Amount */}
            <div data-id="element-5777">
              <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-5778">
                Your Offer Amount (in Lakhs)
              </label>
              <div className="relative" data-id="element-5779">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" data-id="element-5780" />
                <input type="number" step="0.01" required value={offerAmount} onChange={e => setOfferAmount(e.target.value)} placeholder="45.00" className="w-full pl-12 pr-4 py-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500 text-lg font-mono" data-id="element-5781" />
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5782">
                Minimum offer: ₹36,00,000 (80% of listing price)
              </p>
            </div>

            {/* Earnest Money */}
            <div data-id="element-5783">
              <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-5784">
                Earnest Money Deposit (%)
              </label>
              <select value={earnestMoney} onChange={e => setEarnestMoney(e.target.value)} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-5785">
                <option value="5" data-id="element-5786">
                  5% - ₹{(offerValue * 5 / 100).toLocaleString('en-IN')}
                </option>
                <option value="10" data-id="element-5787">
                  10% - ₹{(offerValue * 10 / 100).toLocaleString('en-IN')}
                </option>
                <option value="15" data-id="element-5788">
                  15% - ₹{(offerValue * 15 / 100).toLocaleString('en-IN')}
                </option>
                <option value="20" data-id="element-5789">
                  20% - ₹{(offerValue * 20 / 100).toLocaleString('en-IN')}
                </option>
              </select>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5790">
                This amount will be held in escrow until transaction completes
              </p>
            </div>

            {/* Message to Seller */}
            <div data-id="element-5791">
              <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-5792">
                Message to Seller (Optional)
              </label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Explain your offer, timeline, or any conditions..." rows={4} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500 text-sm" data-id="element-5793" />
            </div>

            {/* Summary */}
            {offerValue > 0 && <div className="p-4 bg-blue-50 border border-blue-200 space-y-2" data-id="element-5794">
                <h4 className="font-bold text-sm mb-3" data-id="element-5795">Offer Summary</h4>
                <div className="flex justify-between text-sm" data-id="element-5796">
                  <span className="text-gray-700" data-id="element-5797">Offer Amount</span>
                  <span className="font-mono font-bold" data-id="element-5798">
                    ₹{offerValue.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-5799">
                  <span className="text-gray-700" data-id="element-5800">
                    Earnest Money ({earnestMoney}%)
                  </span>
                  <span className="font-mono font-bold" data-id="element-5801">
                    ₹{earnestMoneyAmount.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-blue-300" data-id="element-5802">
                  <span className="font-bold" data-id="element-5803">Due Now</span>
                  <span className="font-mono font-bold text-lg" data-id="element-5804">
                    ₹{earnestMoneyAmount.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>}

            {/* Warning */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 flex gap-3" data-id="element-5805">
              <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" data-id="element-5806" />
              <div className="text-xs text-yellow-900" data-id="element-5807">
                <p className="font-bold mb-1" data-id="element-5808">Important:</p>
                <ul className="list-disc list-inside space-y-1" data-id="element-5809">
                  <li data-id="element-5810">
                    Your offer is legally binding once accepted by the seller
                  </li>
                  <li data-id="element-5811">Earnest money is non-refundable if you back out</li>
                  <li data-id="element-5812">Seller has 48 hours to respond to your offer</li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4" data-id="element-5813">
              <button type="button" onClick={() => navigate(-1)} className="px-6 py-3 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-5814">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="px-6 py-3 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-50" data-id="element-5815">
                {isSubmitting ? 'Submitting...' : 'Submit Offer'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title="Offer Submitted" message="Your offer has been sent to the seller. You'll be notified when they respond. Earnest money will be collected if offer is accepted." type="success" data-id="element-5816" />
    </DashboardLayout>;
}